import React, { useMemo } from "react";
import {
  useIcon,
  normalizeNumbers,
  tableCellTypesEnum,
  TableCell,
  Table,
} from "@SeemplicityDev/shared";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../General/useSeverityAsPriority";
import useAddRef, { UseAddRefOptions } from "../useAddRef";
import { createColumnHelper } from "@tanstack/react-table";
interface TopCommonIssuesPageProps extends UseAddRefOptions {
  data?: {
    categories: {
      name: string;
      issues: {
        name: string;
        source: string;
        subcategory: string;
        severe_count: {
          severity: number;
          count: number;
        };
      }[];
    }[];
  };
}

interface TableData {
  category: string;
  subcategory: string;
  source: string;
  severe_count: {
    severity: number;
    count: number;
  };
  name: string;
}

const TopCommonIssuesPage: React.FC<TopCommonIssuesPageProps> = ({
  data,
  addRef,
}) => {
  const ref = useAddRef({ addRef });
  const { getDataSourceIcon } = useIcon("LIGHT");
  const { getSeverityField } = useSeverityAsPriority();

  const tableData = useMemo(() => {
    return data?.categories
      ?.reduce((acc: TableData[], category) => {
        category.issues.forEach((issue) => {
          acc.push({
            category: category.name,
            subcategory: issue.subcategory,
            source: issue.source,
            severe_count: issue.severe_count,
            name: issue.name,
          });
        });
        return acc;
      }, [] as TableData[])
      .sort((a, b) => b.severe_count.count - a.severe_count.count);
  }, [data]);

  const columnHelper = createColumnHelper<TableData>();

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("category", {
        cell: (props) => (
          <TableCell>
            {!props.getValue()
              ? "Unknown"
              : props.getValue() === "VM"
                ? "Vulnerability Management"
                : props.getValue()}
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Category",
        size: 80,
      }),
      columnHelper.accessor("subcategory", {
        cell: (props) => <TableCell>{props.getValue()}</TableCell>,
        enableSorting: false,
        enableColumnFilter: false,
        header: "Subcategory",
        size: 80,
      }),
      columnHelper.accessor("source", {
        cell: (props) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <img
              alt={props.getValue()}
              src={getDataSourceIcon(props.getValue(), "S") as string}
              className={"data-source-icon"}
            />
          </TableCell>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        header: "Source",
        size: 30,
      }),
      columnHelper.accessor("severe_count", {
        cell: (props) => {
          const { severity, count } = props.getValue();
          return (
            <TableCell type={tableCellTypesEnum.COMPONENT}>
              <div className={"findings-count-wrapper"}>
                <div
                  className={"severity-dot"}
                  style={{
                    backgroundColor: getSeverityField(
                      severity,
                      SeverityFieldsEnum.COLOR
                    ),
                  }}
                />
                <span>{normalizeNumbers(count)}</span>
              </div>
            </TableCell>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        header: "No. Occurrences",
        size: 48,
      }),
      columnHelper.accessor("name", {
        cell: (props) => <TableCell>{props.getValue()}</TableCell>,
        enableSorting: false,
        enableColumnFilter: false,
        header: "Title",
      }),
    ];
  }, [tableData]);

  return (
    <div className={`eoy-page top-common-issues-page`} ref={ref}>
      <h4>
        <span className="main">Top 10 Most Common Findings</span>{" "}
      </h4>
      <Table
        columns={columns}
        data={tableData}
        className={"top-common-issues-table"}
      />
    </div>
  );
};

export default TopCommonIssuesPage;
